import { type FC, Suspense, lazy } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

const PdpRespSharedSidebarSellerInfoAndContact = lazy(
    () =>
        import(
            /* webpackChunkName: "PdpRespSidebarSellerInfoAndContact" */ './PdpRespSharedSidebarSellerInfoAndContact'
        )
);

import { useSellerRating } from '../../shared/hooks/useSellerRating';
import { getIsNewSeller } from '../../utils/sellerHelpers';
import { useAppSelector } from '../hooks/useReactReduxHooks';
import { useRemoveNegotiation } from '../hooks/useRemoveNegotiationTest';

import { type PdpRespSidebarSellerInfoAndContactLazy_item$key } from './__generated__/PdpRespSidebarSellerInfoAndContactLazy_item.graphql';
import { type PdpRespSidebarSellerInfoAndContactLazy_user$key } from './__generated__/PdpRespSidebarSellerInfoAndContactLazy_user.graphql';
import { type PdpRespSidebarSellerInfoAndContactLazy_seller$key } from './__generated__/PdpRespSidebarSellerInfoAndContactLazy_seller.graphql';

import styles from './PdpRespSidebarSellerInfoAndContactLazy.scss';

const userFragment = graphql`
    fragment PdpRespSidebarSellerInfoAndContactLazy_user on User {
        ...PdpRespSharedSidebarSellerInfoAndContact_user
    }
`;
const sellerFragment = graphql`
    fragment PdpRespSidebarSellerInfoAndContactLazy_seller on Seller {
        monthsSinceStartDate: timeSinceStartDate(unit: MONTH)
        ...PdpRespSharedSidebarSellerInfoAndContact_seller
        ...useSellerRating_seller
    }
`;
const itemFragment = graphql`
    fragment PdpRespSidebarSellerInfoAndContactLazy_item on Item {
        ...PdpRespSharedSidebarSellerInfoAndContact_item
        ...useRemoveNegotiationTest_item
    }
`;

export const PdpRespSidebarSellerInfoAndContactLazy: FC<{
    selectedSkuId: string;
    user: PdpRespSidebarSellerInfoAndContactLazy_user$key | null | undefined;
    seller: PdpRespSidebarSellerInfoAndContactLazy_seller$key;
    item: PdpRespSidebarSellerInfoAndContactLazy_item$key;
    itemQuantity?: number;
    currency: string;
    isClient: boolean;
}> = ({
    selectedSkuId,
    item: itemRef,
    user: userRef,
    seller: sellerRef,
    itemQuantity,
    currency,
    isClient,
}) => {
    const user = useFragment(userFragment, userRef);
    const seller = useFragment(sellerFragment, sellerRef);
    const item = useFragment(itemFragment, itemRef);
    const { isPDPDesktopAggregationVariant } = useAppSelector(
        ({ relayVariables }) => relayVariables.variables || {}
    );

    const isNewSeller = getIsNewSeller(seller.monthsSinceStartDate);
    const rating = useSellerRating({ seller });
    const hasLineBreak = isNewSeller || rating === 0;

    const removeNegotiation = useRemoveNegotiation({ item, currency });

    return (
        <Suspense
            fallback={
                <div
                    className={classnames({
                        [styles.lazy]: !hasLineBreak,
                        [styles.lazyWithLineBreak]: hasLineBreak,
                    })}
                />
            }
        >
            <PdpRespSharedSidebarSellerInfoAndContact
                user={user}
                seller={seller}
                item={item}
                selectedSkuId={selectedSkuId}
                itemQuantity={itemQuantity}
                currency={currency}
                isPDPDesktopAggregationVariant={isPDPDesktopAggregationVariant}
                isClient={isClient}
                removeNegotiation={removeNegotiation}
            />
        </Suspense>
    );
};
